import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useOrders } from '../hooks/useOrders';
import { Page } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Field, Check } from '../components/ui/Field';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Callout } from '../components/ui/Callout';
import { Stepper } from '../components/ui/Stepper';
import { Divider } from '../components/ui/Divider';
import { Icon } from '../components/ui/Icon';
import { StripeCardForm } from './payment/StripeCardForm';
import { PaymentOptionCard } from './payment/PaymentOptionCard';
import {
  ApiResponse,
  OrderSummary,
  PaymentDetails,
  STEPS,
  buildOrderPayload,
  computeOrderSummary,
  formatAddressSummary,
  prepareOrderData,
  validatePaymentForm,
} from './payment/shippingPaymentHelpers';

const ShippingPaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { balance, paymentMethods, makePayment } = useWallet();
  const { createOrder, updateOrder } = useOrders();

  const [isLoading, setIsLoading] = useState(true);
  const [orderData, setOrderData] = useState<any>(null);
  const [orderError, setOrderError] = useState('');

  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails>({
    method: 'wallet',
    selectedPaymentMethodId: paymentMethods.find((m) => m.isDefault)?.id,
    cardNumber: '',
    cardholderName: '',
    expiryDate: '',
    cvv: '',
    billingAddress: { country: 'United States', city: '', street: '', zipCode: '' },
    savePaymentMethod: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [orderSummary, setOrderSummary] = useState<OrderSummary>({
    sender: '',
    recipient: '',
    service: '',
    parcel: { weight: '', dimensions: '' },
    price: { shipping: 0, insurance: 0, tax: 0, total: 0 },
    pickupDate: '',
    currency: 'ILS',
    isDocument: false,
  });

  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [hasDraftBeenSaved, setHasDraftBeenSaved] = useState(false);
  const [draftFeedback, setDraftFeedback] = useState<
    { type: 'ok' | 'warn'; message: string } | null
  >(null);

  const [paymentToken, setPaymentToken] = useState<string | null>(null);
  const [billingName, setBillingName] = useState('');
  const [cardProcessingError, setCardProcessingError] = useState<string | null>(null);

  const draftOrderId =
    orderData?.orderId ??
    orderData?.id ??
    orderData?.order_id ??
    orderData?.order?.id ??
    location.state?.orderId ??
    null;
  const allowDraft =
    typeof orderData?.allowDraft === 'boolean' ? orderData.allowDraft : true;
  const canSaveAsDraft = !!orderData && allowDraft && !hasDraftBeenSaved;

  const handleCardAdded = useCallback((success: boolean, result?: string) => {
    if (success && result) {
      setPaymentToken(result);
      setPaymentDetails((p) => ({ ...p, selectedPaymentMethodId: result }));
      setCardProcessingError(null);
    } else {
      setCardProcessingError(result || 'Failed to process card');
      setPaymentToken(null);
    }
  }, []);

  useEffect(() => {
    if (!location.state) {
      setOrderError('No order data received. Please go back and select shipping options.');
      setIsLoading(false);
      return;
    }
    try {
      const prepared = prepareOrderData(location.state);
      setOrderData(prepared);
      setDraftFeedback(null);
      setHasDraftBeenSaved(
        prepared.order_status === 'draft' || prepared.status === 'draft'
      );
      setOrderSummary(computeOrderSummary(prepared));
      setIsLoading(false);
    } catch (err) {
      console.error('Error processing order data:', err);
      setOrderError('Failed to process order data. Please try again.');
      setIsLoading(false);
      setTimeout(() => navigate('/shipping-order'), 3000);
    }
  }, [location.state, navigate]);

  const handleCompletePurchase = async () => {
    if (!termsAccepted) {
      setErrors({ terms: 'Please accept the terms and conditions to proceed.' });
      return;
    }
    if (paymentDetails.method === 'card' && !paymentDetails.selectedPaymentMethodId) {
      const formErrors = validatePaymentForm(
        paymentDetails,
        balance,
        orderSummary.price.total
      );
      if (Object.keys(formErrors).length) {
        setErrors({
          ...formErrors,
          payment: 'Please complete all required fields in the payment form',
        });
        return;
      }
    }
    setIsProcessing(true);
    try {
      const orderPayload = buildOrderPayload(
        orderData,
        orderSummary,
        paymentDetails,
        paymentToken,
        billingName,
        { includePayment: true }
      );
      const isUpdatingDraft = Boolean(draftOrderId) && hasDraftBeenSaved;
      let responseData: any;
      let successFlag = true;
      let successMessage = 'Order completed successfully';
      if (isUpdatingDraft) {
        responseData = await updateOrder(draftOrderId as string | number, orderPayload);
      } else {
        const orderResponse = (await createOrder(orderPayload)) as unknown as ApiResponse;
        successFlag = orderResponse.success !== undefined ? orderResponse.success : successFlag;
        successMessage =
          orderResponse.message || orderResponse.data?.message || successMessage;
        responseData = orderResponse.data || orderResponse;
      }
      if (paymentDetails.method === 'wallet') {
        await makePayment(
          orderSummary.price.total,
          `Shipping to ${orderSummary.recipient}`,
          responseData.orderNumber ||
            responseData.order_number ||
            `ORDER-${Date.now()}`
        );
      }
      navigate('/shipping-confirmation', {
        state: {
          ...responseData,
          orderNumber: responseData.orderNumber || responseData.order_number,
          status: responseData.status || 'confirmed',
          trackingNumber:
            responseData.trackingNumber || responseData.tracking_number,
          carrier:
            responseData.carrier || responseData.provider || orderSummary.carrier,
          service: responseData.service || orderSummary.service,
          orderSummary: {
            ...orderSummary,
            referenceNumber:
              responseData.referenceNumber ||
              responseData.reference_number ||
              orderSummary.referenceNumber,
          },
          success: successFlag,
          message: successMessage,
        },
      });
    } catch (err) {
      console.error('Error processing order:', err);
      const message =
        (err as any)?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Payment processing failed.');
      setErrors({ payment: message });
      setIsProcessing(false);
    }
  };

  const handleSaveAsDraft = async () => {
    if (!orderData) {
      setDraftFeedback({ type: 'warn', message: 'Order details are unavailable.' });
      return;
    }
    if (!canSaveAsDraft) {
      setDraftFeedback({ type: 'warn', message: 'This order has already been saved as a draft.' });
      return;
    }
    setIsSavingDraft(true);
    setDraftFeedback(null);
    try {
      const orderPayload = buildOrderPayload(
        orderData,
        orderSummary,
        paymentDetails,
        paymentToken,
        billingName
      );
      const draftPayload = { ...orderPayload, save_as_draft: true };
      const orderResponse = (await createOrder(draftPayload)) as unknown as ApiResponse;
      const responseData = orderResponse.data || orderResponse;
      setHasDraftBeenSaved(true);
      setOrderData((prev: any) => {
        const resolvedId = responseData.id ?? responseData.order_id ?? prev?.id ?? null;
        if (!prev) {
          return {
            ...orderPayload,
            id: resolvedId,
            orderId: resolvedId,
            order_status: responseData.order_status || 'draft',
            status: responseData.status || 'draft',
            allowDraft: false,
          };
        }
        return {
          ...prev,
          id: resolvedId ?? prev.id,
          orderId: resolvedId ?? prev.orderId,
          order_status: responseData.order_status || 'draft',
          status: responseData.status || 'draft',
          order_number: responseData.order_number ?? prev.order_number,
          referenceNumber:
            responseData.reference_number ||
            responseData.referenceNumber ||
            prev.referenceNumber,
          allowDraft: false,
        };
      });
      setOrderSummary((prev) => ({
        ...prev,
        referenceNumber:
          responseData.reference_number ||
          responseData.referenceNumber ||
          prev.referenceNumber,
      }));
      setDraftFeedback({
        type: 'ok',
        message:
          orderResponse.message ||
          responseData.message ||
          'Order saved as draft. You can finish it later from My Orders.',
      });
    } catch (err) {
      console.error('Error saving draft:', err);
      const message =
        (err as any)?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Failed to save draft.');
      setDraftFeedback({ type: 'warn', message });
    } finally {
      setIsSavingDraft(false);
    }
  };

  if (isLoading) {
    return (
      <Page width="narrow">
        <div className="muted" style={{ textAlign: 'center', padding: 64 }}>
          Loading payment details…
        </div>
      </Page>
    );
  }

  if (orderError) {
    return (
      <Page width="narrow">
        <Callout tone="warn">
          <Icon name="alertCircle" size={16} />
          <span>{orderError}</span>
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={() => navigate('/shipping-order')}
            style={{ marginInlineStart: 'auto' }}
          >
            Back
          </button>
        </Callout>
      </Page>
    );
  }

  return (
    <Page width="narrow">
      <div style={{ marginBottom: 24 }}>
        <Stepper
          steps={STEPS.map((label, i) => ({
            label,
            status: (i < 2 ? 'done' : i === 2 ? 'active' : 'pending') as
              | 'done'
              | 'active'
              | 'pending',
          }))}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/shipping-order', { state: orderData })}
        >
          <Icon name="arrowLeft" size={14} />
          <span>Back</span>
        </Button>
      </div>

      <div
        style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 20 }}
        className="details-grid"
      >
        <Card pad style={{ padding: 28 }}>
          <h2 style={{ fontSize: 20 }}>Payment method</h2>

          {(errors.payment || cardProcessingError) && (
            <div style={{ marginTop: 14 }}>
              <Callout tone="warn">
                <Icon name="alertCircle" size={16} />
                <span>{errors.payment || cardProcessingError}</span>
              </Callout>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
            <PaymentOptionCard
              active={paymentDetails.method === 'wallet'}
              onClick={() => setPaymentDetails((p) => ({ ...p, method: 'wallet' }))}
              icon="wallet"
              title="Almaship wallet"
              sub={`Balance: ${orderSummary.currency} ${balance.toFixed(2)} · auto-convert enabled`}
              right={<Badge tone="ok">Recommended</Badge>}
            />
            {paymentMethods.map((m) => (
              <PaymentOptionCard
                key={m.id}
                active={
                  paymentDetails.method === 'card' &&
                  paymentDetails.selectedPaymentMethodId === m.id
                }
                onClick={() =>
                  setPaymentDetails((p) => ({
                    ...p,
                    method: 'card',
                    selectedPaymentMethodId: m.id,
                  }))
                }
                icon="card"
                title={m.name || `Card ending ${m.last4}`}
                sub={m.isDefault ? 'Default · saved card' : 'Saved card'}
              />
            ))}
            <PaymentOptionCard
              active={
                paymentDetails.method === 'card' && !paymentDetails.selectedPaymentMethodId
              }
              onClick={() =>
                setPaymentDetails((p) => ({
                  ...p,
                  method: 'card',
                  selectedPaymentMethodId: undefined,
                }))
              }
              icon="plus"
              title="Add new card"
              sub="Visa, Mastercard, AmEx via Stripe"
            />
          </div>

          {paymentDetails.method === 'card' && !paymentDetails.selectedPaymentMethodId && (
            <div style={{ marginTop: 20 }}>
              <h3 style={{ fontSize: 14, marginBottom: 12 }}>Card details</h3>
              <StripeCardForm
                onCardAdded={handleCardAdded}
                billingName={billingName}
                setBillingName={setBillingName}
              />
            </div>
          )}

          {paymentDetails.method === 'wallet' && balance < orderSummary.price.total && (
            <div style={{ marginTop: 16 }}>
              <Callout tone="warn">
                <Icon name="alertCircle" size={16} />
                <span>
                  Insufficient wallet balance. You need at least{' '}
                  <strong>
                    {orderSummary.currency} {orderSummary.price.total.toFixed(2)}
                  </strong>
                  . Top up from your wallet first.
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  onClick={() => navigate('/wallet')}
                  style={{ marginInlineStart: 'auto' }}
                >
                  Open wallet
                </button>
              </Callout>
            </div>
          )}

          <div style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: 14, marginBottom: 12 }}>Billing address</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
              <Field label="Street" hint={errors['billingAddress.street']}>
                <Input
                  value={paymentDetails.billingAddress.street}
                  onChange={(e) =>
                    setPaymentDetails((p) => ({
                      ...p,
                      billingAddress: { ...p.billingAddress, street: e.target.value },
                    }))
                  }
                />
              </Field>
              <Field label="City" hint={errors['billingAddress.city']}>
                <Input
                  value={paymentDetails.billingAddress.city}
                  onChange={(e) =>
                    setPaymentDetails((p) => ({
                      ...p,
                      billingAddress: { ...p.billingAddress, city: e.target.value },
                    }))
                  }
                />
              </Field>
              <Field label="ZIP" hint={errors['billingAddress.zipCode']}>
                <Input
                  value={paymentDetails.billingAddress.zipCode}
                  onChange={(e) =>
                    setPaymentDetails((p) => ({
                      ...p,
                      billingAddress: { ...p.billingAddress, zipCode: e.target.value },
                    }))
                  }
                />
              </Field>
            </div>
          </div>

          <Callout tone="ok" style={{ marginTop: 20 }}>
            <Icon name="checkCircle" size={16} />
            <span>
              <strong>Included:</strong> insurance, label printing, pickup scheduling, 24/7
              tracking, proof of delivery.
            </span>
          </Callout>

          <div style={{ marginTop: 18 }}>
            <Check>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <span>
                I accept the{' '}
                <a href="/terms" target="_blank" rel="noreferrer">
                  Terms
                </a>{' '}
                and{' '}
                <a href="/privacy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                .
              </span>
            </Check>
            {errors.terms && (
              <div className="muted" style={{ color: 'var(--err)', fontSize: 12, marginTop: 4 }}>
                {errors.terms}
              </div>
            )}
          </div>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card pad style={{ padding: 20, position: 'sticky', top: 96 }}>
            <div
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 12 }}
            >
              YOU'RE PAYING
            </div>
            <div
              className="mono"
              style={{ fontSize: 36, fontWeight: 600, letterSpacing: '-0.02em' }}
            >
              {orderSummary.currency} {orderSummary.price.total.toFixed(2)}
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              {orderSummary.carrier || 'Carrier'} {orderSummary.service}
            </div>

            <div style={{ marginTop: 14 }}>
              {[
                ['Shipping', orderSummary.price.shipping],
                ['Insurance', orderSummary.price.insurance],
                ['Tax', orderSummary.price.tax],
              ].map(([label, value]) => (
                <div
                  key={label as string}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '4px 0',
                    fontSize: 13,
                  }}
                >
                  <span className="muted">{label}</span>
                  <span className="mono">
                    {orderSummary.currency} {Number(value).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            <Divider style={{ margin: '12px 0' }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
              <div className="muted">From</div>
              <div style={{ fontWeight: 500 }}>{formatAddressSummary(orderSummary.sender)}</div>
              <div className="muted" style={{ marginTop: 6 }}>To</div>
              <div style={{ fontWeight: 500 }}>{formatAddressSummary(orderSummary.recipient)}</div>
            </div>

            <Button
              variant="blue"
              block
              size="lg"
              style={{ marginTop: 18 }}
              onClick={handleCompletePurchase}
              disabled={isProcessing}
            >
              <Icon name="lock" size={14} />
              <span>{isProcessing ? 'Processing…' : 'Pay & book'}</span>
            </Button>

            {canSaveAsDraft && (
              <Button
                block
                size="sm"
                variant="ghost"
                style={{ marginTop: 8 }}
                onClick={handleSaveAsDraft}
                disabled={isSavingDraft}
              >
                <Icon name="file" size={13} />
                <span>{isSavingDraft ? 'Saving…' : 'Save as draft'}</span>
              </Button>
            )}

            {draftFeedback && (
              <div style={{ marginTop: 10 }}>
                <Callout tone={draftFeedback.type}>
                  <Icon
                    name={draftFeedback.type === 'ok' ? 'checkCircle' : 'alertCircle'}
                    size={14}
                  />
                  <span style={{ fontSize: 12 }}>{draftFeedback.message}</span>
                </Callout>
              </div>
            )}

            <div
              className="muted"
              style={{ fontSize: 11, textAlign: 'center', marginTop: 12 }}
            >
              <Icon name="shield" size={11} /> Secure checkout · Insured up to $100
            </div>
          </Card>
        </div>
      </div>
    </Page>
  );
};

export default ShippingPaymentPage;
